import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SellerApplication = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    // Company Information
    companyName: '',
    registrationNumber: '',
    yearEstablished: '',
    companyType: '',
    website: '',
    
    // Contact Information
    primaryContactName: '',
    designation: '',
    email: '',
    phone: '',
    whatsapp: '',
    
    // Business Address
    address: '',
    city: '',
    state: '',
    country: '',
    postalCode: '',
    
    // Business Details
    businessType: '',
    annualExportVolume: '',
    commoditiesSupplied: '',
    exportMarkets: '',
    productionCapacity: '',
    
    // Trade References
    reference1Company: '',
    reference1Contact: '',
    reference1Phone: '',
    reference2Company: '',
    reference2Contact: '',
    reference2Phone: '',
    
    // Banking Information
    bankName: '',
    bankBranch: '',
    bankCountry: '',
    
    // Quality & Compliance
    qualityCertifications: '',
    exportLicenses: '',
    factoryLocation: '',
    warehouseFacilities: '',
    additionalInfo: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Transform form data to match backend schema
      const applicationData = {
        companyName: formData.companyName,
        businessType: formData.businessType || formData.companyType,
        country: formData.country,
        yearsInBusiness: formData.yearEstablished ? String(new Date().getFullYear() - parseInt(formData.yearEstablished)) : null,
        annualExportVolume: formData.annualExportVolume,
        primaryProducts: formData.commoditiesSupplied ? formData.commoditiesSupplied.split(',').map(s => s.trim()) : [],
        certifications: formData.qualityCertifications ? formData.qualityCertifications.split(',').map(s => s.trim()) : [],
        exportMarkets: formData.exportMarkets ? formData.exportMarkets.split(',').map(s => s.trim()) : [],
        manufacturingCapacity: formData.productionCapacity || null,
        contactName: formData.primaryContactName,
        contactTitle: formData.designation,
        email: formData.email,
        phone: formData.phone,
        whatsapp: formData.whatsapp || null,
        website: formData.website || null,
        additionalInfo: formData.additionalInfo || null
      };

      const response = await axios.post(`${API_URL}/api/apply/seller`, applicationData);
      
      if (response.data.success) {
        toast({
          title: "Application Submitted Successfully!",
          description: response.data.message,
        });

        // Reset form
        setFormData({
          companyName: '',
          registrationNumber: '',
          yearEstablished: '',
          companyType: '',
          website: '',
          primaryContactName: '',
          designation: '',
          email: '',
          phone: '',
          whatsapp: '',
          address: '',
          city: '',
          state: '',
          country: '',
          postalCode: '',
          businessType: '',
          annualExportVolume: '',
          commoditiesSupplied: '',
          exportMarkets: '',
          productionCapacity: '',
          reference1Company: '',
          reference1Contact: '',
          reference1Phone: '',
          reference2Company: '',
          reference2Contact: '',
          reference2Phone: '',
          bankName: '',
          bankBranch: '',
          bankCountry: '',
          qualityCertifications: '',
          exportLicenses: '',
          factoryLocation: '',
          warehouseFacilities: '',
          additionalInfo: ''
        });
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "There was an error submitting your application. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {/* Hero */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/verified-directory" className="inline-flex items-center text-[#263776] hover:text-[#F47D20] mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Verified Directory
            </Link>
            <div className="text-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_spanx-export/artifacts/xlq8jb39_SPANXPORT%20Logo%20Master.png" 
                alt="SPANXPORT" 
                className="h-16 mx-auto mb-2"
              />
              <p className="text-base text-[#918f90] font-bold tracking-wide mb-6" style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 700 }}>
                Powering Intelligent Global Trade
              </p>
              <h1 className="text-5xl font-bold mb-4">Apply as Seller</h1>
              <p className="text-xl text-slate-700">
                Join SPANXPORT's Verified Directory to access verified buyers and grow your export business
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="p-8">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Company Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Company Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="companyName">Company Name *</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="registrationNumber">Registration Number *</Label>
                      <Input
                        id="registrationNumber"
                        name="registrationNumber"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="yearEstablished">Year Established *</Label>
                      <Input
                        id="yearEstablished"
                        name="yearEstablished"
                        type="number"
                        value={formData.yearEstablished}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="companyType">Company Type *</Label>
                      <Input
                        id="companyType"
                        name="companyType"
                        placeholder="e.g., Exporter, Manufacturer, Processor"
                        value={formData.companyType}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="website">Company Website</Label>
                      <Input
                        id="website"
                        name="website"
                        type="url"
                        placeholder="https://"
                        value={formData.website}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Primary Contact Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="primaryContactName">Full Name *</Label>
                      <Input
                        id="primaryContactName"
                        name="primaryContactName"
                        value={formData.primaryContactName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="designation">Designation *</Label>
                      <Input
                        id="designation"
                        name="designation"
                        value={formData.designation}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="With country code"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="whatsapp">WhatsApp Number</Label>
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        type="tel"
                        placeholder="With country code"
                        value={formData.whatsapp}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>

                {/* Business Address */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Business Address</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <Label htmlFor="address">Street Address *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="state">State/Province *</Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="country">Country *</Label>
                      <Input
                        id="country"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="postalCode">Postal Code *</Label>
                      <Input
                        id="postalCode"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Business Details */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Business Details</h2>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <Label htmlFor="businessType">Type of Business *</Label>
                      <Input
                        id="businessType"
                        name="businessType"
                        placeholder="e.g., Exporter, Manufacturer, Processor, Agricultural Cooperative"
                        value={formData.businessType}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="annualExportVolume">Annual Export Volume *</Label>
                      <Input
                        id="annualExportVolume"
                        name="annualExportVolume"
                        placeholder="e.g., 1000 MT, $1M-$5M"
                        value={formData.annualExportVolume}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="commoditiesSupplied">Commodities You Supply *</Label>
                      <Textarea
                        id="commoditiesSupplied"
                        name="commoditiesSupplied"
                        placeholder="List all commodities you can supply with specifications"
                        value={formData.commoditiesSupplied}
                        onChange={handleChange}
                        rows={3}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="exportMarkets">Current Export Markets *</Label>
                      <Input
                        id="exportMarkets"
                        name="exportMarkets"
                        placeholder="e.g., UAE, UK, South Africa, Singapore"
                        value={formData.exportMarkets}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="productionCapacity">Production/Supply Capacity *</Label>
                      <Input
                        id="productionCapacity"
                        name="productionCapacity"
                        placeholder="e.g., 500 MT per month"
                        value={formData.productionCapacity}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Trade References */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Trade References</h2>
                  <p className="text-sm text-slate-600 mb-4">Provide at least 2 trade references from buyers or partners</p>
                  
                  <div className="space-y-6">
                    <div className="border-l-4 border-[#F47D20] pl-4">
                      <h3 className="font-semibold mb-3">Reference 1</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="reference1Company">Company Name *</Label>
                          <Input
                            id="reference1Company"
                            name="reference1Company"
                            value={formData.reference1Company}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="reference1Contact">Contact Person *</Label>
                          <Input
                            id="reference1Contact"
                            name="reference1Contact"
                            value={formData.reference1Contact}
                            onChange={handleChange}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="reference1Phone">Phone Number *</Label>
                          <Input
                            id="reference1Phone"
                            name="reference1Phone"
                            type="tel"
                            value={formData.reference1Phone}
                            onChange={handleChange}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    <div className="border-l-4 border-[#263776] pl-4">
                      <h3 className="font-semibold mb-3">Reference 2</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="reference2Company">Company Name</Label>
                          <Input
                            id="reference2Company"
                            name="reference2Company"
                            value={formData.reference2Company}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <Label htmlFor="reference2Contact">Contact Person</Label>
                          <Input
                            id="reference2Contact"
                            name="reference2Contact"
                            value={formData.reference2Contact}
                            onChange={handleChange}
                          />
                        </div>
                        <div>
                          <Label htmlFor="reference2Phone">Phone Number</Label>
                          <Input
                            id="reference2Phone"
                            name="reference2Phone"
                            type="tel"
                            value={formData.reference2Phone}
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Banking Information */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Banking Information</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="bankName">Bank Name *</Label>
                      <Input
                        id="bankName"
                        name="bankName"
                        value={formData.bankName}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="bankBranch">Branch/Location</Label>
                      <Input
                        id="bankBranch"
                        name="bankBranch"
                        value={formData.bankBranch}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="bankCountry">Bank Country *</Label>
                      <Input
                        id="bankCountry"
                        name="bankCountry"
                        value={formData.bankCountry}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Quality & Compliance */}
                <div>
                  <h2 className="text-2xl font-bold text-[#263776] mb-6">Quality & Compliance</h2>
                  <div className="space-y-6">
                    <div>
                      <Label htmlFor="qualityCertifications">Quality Certifications *</Label>
                      <Textarea
                        id="qualityCertifications"
                        name="qualityCertifications"
                        placeholder="e.g., ISO 9001, HACCP, Halal, Organic, FSSAI, etc."
                        value={formData.qualityCertifications}
                        onChange={handleChange}
                        rows={2}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="exportLicenses">Export Licenses/Registration Numbers *</Label>
                      <Textarea
                        id="exportLicenses"
                        name="exportLicenses"
                        placeholder="List IEC, APEDA, FSSAI or other relevant export licenses"
                        value={formData.exportLicenses}
                        onChange={handleChange}
                        rows={2}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="factoryLocation">Factory/Processing Unit Location</Label>
                      <Input
                        id="factoryLocation"
                        name="factoryLocation"
                        placeholder="City, State, Country"
                        value={formData.factoryLocation}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="warehouseFacilities">Warehouse/Cold Storage Facilities</Label>
                      <Textarea
                        id="warehouseFacilities"
                        name="warehouseFacilities"
                        placeholder="Describe your storage facilities and capacity"
                        value={formData.warehouseFacilities}
                        onChange={handleChange}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label htmlFor="additionalInfo">Additional Information</Label>
                      <Textarea
                        id="additionalInfo"
                        name="additionalInfo"
                        placeholder="Any other relevant information about your business, capabilities, or special offerings"
                        value={formData.additionalInfo}
                        onChange={handleChange}
                        rows={3}
                      />
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <p className="text-sm text-slate-600 mb-6">
                    By submitting this application, you agree that SPANXPORT may verify the information provided and contact your references.
                  </p>
                  <div className="flex gap-4">
                    <Button 
                      type="submit" 
                      size="lg" 
                      className="bg-[#263776] text-white hover:bg-[#1e2a5e]"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        'Submit Application'
                      )}
                    </Button>
                    <Button asChild size="lg" variant="outline">
                      <Link to="/verified-directory">Cancel</Link>
                    </Button>
                  </div>
                </div>
              </form>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SellerApplication;